import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Phone, Clock, Users, ShieldCheck, CheckCircle2 } from 'lucide-react';

// /support — "Have a specific question?" contact form, rebuilt
// 2026-05-24 from the user's mockup screenshots to replace the
// legacy ContactForm. 2-col grid (Name + Email, Phone + Topic),
// then full-width Project Address (optional) and Message, then
// consent checkbox + Send button + "Or call instead" link. Trust
// strip below the form: Quick Response / Real Roofers / Secure
// & Private.
//
// Email wiring INTENTIONALLY parked — pending CTO discussion +
// Resend domain verification. Submit handler currently does
// client-side validation, logs the payload, and shows a success
// state. Replace the TODO with a contact-submit Edge Function
// call once provisioning is done.
//
// The section id="contact-form" stays — the "Email Us" card on
// "Reach a real roofer" smooth-scrolls to it.

const PHONE_TEL = '9379108791';
const PHONE_DISPLAY = '937-910-8791';

const TOPIC_OPTIONS = [
  { value: '', label: 'Select a topic', disabled: true },
  { value: 'shopping', label: "I'm shopping for a roof" },
  { value: 'pricing', label: 'Pricing questions' },
  { value: 'in-progress', label: 'I have a project in progress' },
  { value: 'warranty', label: 'Warranty / claim' },
  { value: 'insurance', label: 'Insurance / storm damage' },
  { value: 'other', label: 'Something else' },
];

const inputBaseClass =
  'w-full rounded-lg px-4 py-3 text-[15px] text-gray-900 placeholder-gray-400 outline-none transition-colors duration-200 focus:ring-2 focus:ring-cyan-400/40';

const inputStyle = {
  background: '#F1F5F9',
  border: '1px solid transparent',
};

const Label = ({ htmlFor, children, required = false, optional = false }) => (
  <label
    htmlFor={htmlFor}
    className="block text-sm font-bold mb-2"
    style={{ color: '#0a1628' }}
  >
    {children}
    {required && <span className="ml-0.5" style={{ color: '#dc2626' }}>*</span>}
    {optional && <span className="ml-1 font-normal text-gray-400">(optional)</span>}
  </label>
);

const TRUST_ITEMS = [
  { Icon: Clock, title: 'Quick Response', body: 'Within one business day' },
  { Icon: Users, title: 'Real Roofers', body: 'Not a call center' },
  { Icon: ShieldCheck, title: 'Secure & Private', body: 'Your information is protected' },
];

const TrustItem = ({ Icon, title, body }) => (
  <div className="flex flex-col items-center text-center">
    <div
      className="flex items-center justify-center rounded-full mb-3"
      style={{
        width: '44px',
        height: '44px',
        background: 'rgba(7, 176, 233, 0.10)',
        border: '1px solid rgba(7, 176, 233, 0.22)',
      }}
      aria-hidden="true"
    >
      <Icon size={20} strokeWidth={2.2} style={{ color: '#07B0E9' }} />
    </div>
    <p className="font-bold text-sm" style={{ color: '#0a1628' }}>{title}</p>
    <p className="text-xs text-gray-500 mt-1">{body}</p>
  </div>
);

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    topic: '',
    address: '',
    message: '',
    agreed: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const setField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = 'Required';
    if (!formData.email.trim()) {
      next.email = 'Required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      next.email = 'Enter a valid email';
    }
    if (!formData.topic) next.topic = 'Required';
    if (!formData.message.trim()) next.message = 'Required';
    if (!formData.agreed) next.agreed = 'Required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    // TODO(email-wiring): replace this with a POST to the
    // contact-submit Edge Function once Resend is provisioned
    // (domain verified + RESEND_API_KEY secret set). Until then,
    // log locally so the user can confirm the form path works.
    console.log('[ContactForm] submission (not yet sent):', {
      ...formData,
      submittedAt: new Date().toISOString(),
    });

    // Simulate a 400ms async submit for the UI affordance
    await new Promise((r) => setTimeout(r, 400));
    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <section id="contact-form" className="py-16 lg:py-24 bg-white">
        <div className="max-w-2xl mx-auto px-5 lg:px-8 text-center">
          <div
            className="inline-flex items-center justify-center rounded-full mb-6"
            style={{
              width: '64px',
              height: '64px',
              background: 'rgba(16, 185, 129, 0.12)',
              border: '1px solid rgba(16, 185, 129, 0.30)',
            }}
          >
            <CheckCircle2 size={32} strokeWidth={2.2} style={{ color: '#10B981' }} />
          </div>
          <h2
            className="font-black leading-[1.05] tracking-tight mb-4"
            style={{
              fontSize: 'clamp(28px, 3.5vw, 42px)',
              fontFamily: "'Poppins', 'Montserrat', sans-serif",
              letterSpacing: '-0.025em',
              color: '#0a1628',
            }}
          >
            Thanks — your message is in.
          </h2>
          <p className="text-base lg:text-lg text-gray-500 leading-relaxed mb-8">
            A real OpenbooQ roofer will get back to you within one business day. If it's urgent, you can also call us at{' '}
            <a href={`tel:${PHONE_TEL}`} className="font-semibold" style={{ color: '#07B0E9' }}>
              {PHONE_DISPLAY}
            </a>.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="contact-form" className="py-16 lg:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-10 lg:mb-12">
          <div className="mb-5 flex justify-center">
            <div
              className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
              style={{
                background: 'rgba(7, 176, 233, 0.10)',
                border: '1px solid rgba(7, 176, 233, 0.30)',
                color: '#0a6eb8',
              }}
            >
              Send a Message
            </div>
          </div>
          <h2
            className="font-black leading-[1.05] tracking-tight mb-4 mx-auto"
            style={{
              fontSize: 'clamp(32px, 4.5vw, 52px)',
              fontFamily: "'Poppins', 'Montserrat', sans-serif",
              letterSpacing: '-0.025em',
              color: '#0a1628',
              maxWidth: '760px',
            }}
          >
            Have a specific{' '}
            <span style={{ color: '#07B0E9' }}>question?</span>
          </h2>
          <p className="text-base lg:text-lg text-gray-500 max-w-xl mx-auto leading-relaxed">
            Fill out the form and a real OpenbooQ roofer gets back to you within one business day.
          </p>
        </div>

        {/* Form card */}
        <form
          onSubmit={handleSubmit}
          className="rounded-2xl bg-white p-6 sm:p-8 lg:p-10"
          style={{
            border: '1px solid #e5ebf0',
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04), 0 14px 36px rgba(15, 23, 42, 0.07)',
          }}
          noValidate
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Full Name */}
            <div>
              <Label htmlFor="cf-name" required>Full Name</Label>
              <input
                id="cf-name"
                type="text"
                placeholder="Enter your full name"
                className={inputBaseClass}
                style={inputStyle}
                value={formData.name}
                onChange={(e) => setField('name', e.target.value)}
              />
              {errors.name && <p className="text-xs mt-1.5" style={{ color: '#dc2626' }}>{errors.name}</p>}
            </div>

            {/* Email Address */}
            <div>
              <Label htmlFor="cf-email" required>Email Address</Label>
              <input
                id="cf-email"
                type="email"
                placeholder="you@example.com"
                className={inputBaseClass}
                style={inputStyle}
                value={formData.email}
                onChange={(e) => setField('email', e.target.value)}
              />
              {errors.email && <p className="text-xs mt-1.5" style={{ color: '#dc2626' }}>{errors.email}</p>}
            </div>

            {/* Phone Number */}
            <div>
              <Label htmlFor="cf-phone">Phone Number</Label>
              <input
                id="cf-phone"
                type="tel"
                placeholder="(937) 555-1234"
                className={inputBaseClass}
                style={inputStyle}
                value={formData.phone}
                onChange={(e) => setField('phone', e.target.value)}
              />
            </div>

            {/* Topic */}
            <div>
              <Label htmlFor="cf-topic" required>What's this about?</Label>
              <select
                id="cf-topic"
                className={inputBaseClass}
                style={inputStyle}
                value={formData.topic}
                onChange={(e) => setField('topic', e.target.value)}
              >
                {TOPIC_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.topic && <p className="text-xs mt-1.5" style={{ color: '#dc2626' }}>{errors.topic}</p>}
            </div>
          </div>

          {/* Project Address — full width */}
          <div className="mt-5">
            <Label htmlFor="cf-address" optional>Project Address</Label>
            <input
              id="cf-address"
              type="text"
              placeholder="123 Main St, City, OH"
              className={inputBaseClass}
              style={inputStyle}
              value={formData.address}
              onChange={(e) => setField('address', e.target.value)}
            />
            <p className="text-xs text-gray-400 mt-1.5">
              Include this if your question is about a specific property.
            </p>
          </div>

          {/* Message — full width */}
          <div className="mt-5">
            <Label htmlFor="cf-message" required>Message</Label>
            <textarea
              id="cf-message"
              rows={5}
              placeholder="Tell us what you need help with..."
              className={`${inputBaseClass} resize-y min-h-[120px]`}
              style={inputStyle}
              value={formData.message}
              onChange={(e) => setField('message', e.target.value)}
            />
            {errors.message && <p className="text-xs mt-1.5" style={{ color: '#dc2626' }}>{errors.message}</p>}
          </div>

          {/* Consent checkbox */}
          <div className="mt-6 flex items-start gap-2.5">
            <input
              id="cf-agreed"
              type="checkbox"
              checked={formData.agreed}
              onChange={(e) => setField('agreed', e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-gray-300 text-cyan-500 focus:ring-cyan-400/40 cursor-pointer"
            />
            <label htmlFor="cf-agreed" className="text-sm text-gray-600 leading-snug cursor-pointer">
              I agree to the{' '}
              <Link to="/terms-of-service" className="font-semibold" style={{ color: '#07B0E9' }}>
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy-policy" className="font-semibold" style={{ color: '#07B0E9' }}>
                Privacy Policy
              </Link>
              , and consent to being contacted about my inquiry.
            </label>
          </div>
          {errors.agreed && (
            <p className="text-xs mt-1.5 ml-7" style={{ color: '#dc2626' }}>
              Please agree to the terms before sending.
            </p>
          )}

          {/* Submit row */}
          <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              type="submit"
              disabled={submitting}
              className="group inline-flex items-center justify-center gap-2 font-bold text-white px-7 py-3.5 rounded-full text-[15px] transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              style={{
                background: '#07B0E9',
                boxShadow: '0 10px 22px rgba(7, 176, 233, 0.30)',
              }}
            >
              {submitting ? 'Sending...' : 'Send message'}
              {!submitting && (
                <Send
                  size={16}
                  strokeWidth={2.6}
                  className="transition-transform duration-200 group-hover:translate-x-1"
                  aria-hidden="true"
                />
              )}
            </button>

            <a
              href={`tel:${PHONE_TEL}`}
              className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors duration-200"
            >
              <Phone size={15} strokeWidth={2.4} aria-hidden="true" />
              Or call instead
            </a>
          </div>
        </form>

        {/* Trust strip — 3 columns below the form */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 mt-10 lg:mt-12">
          {TRUST_ITEMS.map((item) => (
            <TrustItem key={item.title} Icon={item.Icon} title={item.title} body={item.body} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
